'use client';
import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Benefit {
  id: string;
  title: string;
  description: string;
  icon: string;
  stat: string;
  statLabel: string;
}

export default function WhyChooseUs() {
  const [counters, setCounters] = useState<Record<string, number>>({});
  const sectionRef = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  const benefits: Benefit[] = [
  {
    id: 'experience',
    title: 'Proven Experience',
    description: 'Over a decade of delivering exceptional digital solutions in Ulhasnagar and across Maharashtra.',
    icon: 'TrophyIcon',
    stat: '10',
    statLabel: 'Years'
  },
  {
    id: 'projects',
    title: 'Successful Projects',
    description: 'Hundreds of completed projects across diverse industries.',
    icon: 'RocketLaunchIcon',
    stat: '500',
    statLabel: 'Projects'
  },
  {
    id: 'satisfaction',
    title: 'Client Satisfaction',
    description: 'Industry-leading satisfaction rate from happy clients.',
    icon: 'StarIcon',
    stat: '95',
    statLabel: 'Satisfaction'
  },
  {
    id: 'support',
    title: '24/7 Support',
    description: 'Round-the-clock support for all your digital needs.',
    icon: 'ClockIcon',
    stat: '24',
    statLabel: 'Support'
  }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            benefits.forEach((benefit) => {
              animateCounter(benefit.id, parseInt(benefit.stat));
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounter = (id: string, target: number) => {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      setCounters((prev) => ({ ...prev, [id]: Math.floor(current) }));
    }, duration / steps);
  };

  return (
    <section ref={sectionRef} className="py-20 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal animate-fade-in">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
              Why Choose Us
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Excellence in Every<br />Digital Solution
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Trusted by businesses across Ulhasnagar for delivering results that matter.
          </p>
        </div>

        {/* Team Collaboration Images */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12 reveal">
          {[
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7d45ade-1764739915068.png", alt: 'Team collaboration and brainstorming session for creative marketing solutions' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_19fc6a437-1768577164982.png", alt: 'Professional team meeting discussing client projects and strategies' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1858353f5-1768983312834.png", alt: 'Creative team working together on design and marketing campaigns' }]?.
          map((img, idx) =>
          <div
            key={idx}
            className="relative aspect-video rounded-2xl overflow-hidden group hover-lift"
            style={{ animationDelay: `${idx * 120}ms` }}>
            
              <img
              src={img?.src}
              alt={img?.alt}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/40 transition-all" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, idx) =>
          <div
            key={benefit.id}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all glow-hover reveal hover-lift"
            style={{ transitionDelay: `${idx * 100}ms` }}>
            
              <div className="w-16 h-16 mb-6 flex items-center justify-center text-amber-500 bg-amber-500/10 rounded-xl">
                <Icon name={benefit.icon as any} size={32} />
              </div>
              <div className="mb-6">
                <div className="text-5xl font-bold text-white mb-1">
                  {counters[benefit.id] || 0}
                  {benefit.statLabel === 'Years' ? '+' : benefit.statLabel === 'Satisfaction' ? '%' : '+'}
                </div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">
                  {benefit.statLabel}
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">{benefit.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          )}
        </div>

        {/* Success Stories Images */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 reveal">
          {[
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1d33b7424-1770332360022.png", alt: 'Successful business growth and achievement celebration' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc01a4ec-1768322471602.png", alt: 'Business analytics showing successful marketing campaign results' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_18c2f2e9f-1766999716632.png", alt: 'Happy client reviewing successful project outcomes' },
          { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f910061e-1767777727499.png", alt: 'Professional presenting successful digital marketing results' }]?.
          map((img, idx) =>
          <div
            key={idx}
            className="relative aspect-square rounded-xl overflow-hidden group hover-lift"
            style={{ animationDelay: `${idx * 100}ms` }}>
            
              <img
              src={img?.src}
              alt={img?.alt}
              className="w-full h-full object-cover grayscale-[0.4] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700" />
            
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all" />
            </div>
          )}
        </div>
      </div>
    </section>);

}